// Noyau — ANALYSE : dérivée symbolique exacte et limites (réels étendus, formes indéterminées, croissances comparées).
// Rien de métier : les rôles des nœuds sont lus dans la table (eval add, mul, neg, div, pow, sqrt, call, derive) et
// les fonctions par leur eval (exp, ln, sqrt, sin, cos) ; l'infini est la constante dont la valeur est +∞ ;
// la variable d'une dérivée ou d'une limite est la première de `table.lex.calculusVars` présente dans l'expression.
//
// derive(ast, v, table)        → AST de la dérivée (brut : sert à l'ÉVALUATION, jamais montré à l'élève)
// limitValue(expr, point, env) → valeur étendue : rationnel {p,q}, flottant, ±Infinity, ou NaN (pas de limite / FI)
// limitSym(expr, v, point, …)  → { v } (déterminée), { fi } (forme indéterminée), ou null (hors calcul symbolique)

import { num, Q, isLeaf, symbols } from './ast.js';
import { PRIMS, FUNCS, toF, evaluate, constValue } from './equiv.js';

const R_CACHE = new WeakMap();
export function rolesOf(table) {
  let r = R_CACHE.get(table);
  if (r) return r;
  r = {};
  for (const [t, spec] of Object.entries(table.nodes))
    if (spec.eval && !r[spec.eval]) r[spec.eval] = t;
  const fn = (ev) =>
    Object.keys(table.syntax.functions).find((n) => table.syntax.functions[n].eval === ev);
  r.fn = {
    exp: fn('exp'),
    ln: fn('ln'),
    sin: fn('sin'),
    cos: fn('cos'),
    sqrt: fn('sqrt'),
    limit: fn('limit'),
    arctan: fn('arctan'),
    arcsin: fn('arcsin'),
    arccos: fn('arccos'),
  };
  r.inf =
    Object.keys(table.syntax.constants).find(
      (n) => constValue(table.syntax.constants[n]) === Infinity,
    ) ?? null;
  // la constante e (eˣ s'écrit aussi e^x) : valeur de Math.E
  r.e =
    Object.keys(table.syntax.constants).find(
      (n) => Math.abs(table.syntax.constants[n].value - Math.E) < 1e-12,
    ) ?? null;
  R_CACHE.set(table, r);
  return r;
}
const mk = (t, k, name) => (name !== undefined ? { t, k, name } : { t, k });
const isInf = (x) => typeof x === 'number' && !Number.isFinite(x) && !Number.isNaN(x);
const isNum = (x) =>
  (typeof x === 'object' && x !== null && 'p' in x) || (typeof x === 'number' && !Number.isNaN(x));

// variable d'analyse d'une expression (x, t, n… dans l'ordre déclaré par les packs)
export function varOf(n, table) {
  const s = symbols(n);
  for (const v of table.lex.calculusVars || ['x', 't', 'n']) if (s.has(v)) return v;
  return null;
}
const freeOf = (n, v) => !symbols(n).has(v);

// ── Dérivée ───────────────────────────────────────────────────────────────
const DER = new WeakMap();
export function derive(n, v, table) {
  if (!v) return num(0);
  if (!isLeaf(n)) {
    const c = DER.get(n);
    if (c && c.v === v) return c.d;
  }
  const d = derive0(n, v, table);
  if (!isLeaf(n)) DER.set(n, { v, d });
  return d;
}
function derive0(n, v, table) {
  const R = rolesOf(table);
  const D = (x) => derive(x, v, table);
  const zero = num(0),
    one = num(1);
  if (n.t === 'Num') return zero;
  if (n.t === 'Sym') return n.name === v ? one : zero;
  if (freeOf(n, v)) return zero;
  const ev = table.nodes[n.t]?.eval;
  const [a, b] = n.k;
  switch (ev) {
    case 'add':
      return mk(R.add, n.k.map(D));
    case 'neg':
      return mk(R.neg, [D(a)]);
    case 'mul': {
      // (f₁f₂…fₖ)′ = Σ f₁…fᵢ′…fₖ
      const terms = n.k.map((_, i) =>
        mk(
          R.mul,
          n.k.map((f, j) => (i === j ? D(f) : f)),
        ),
      );
      return mk(R.add, terms);
    }
    case 'div':
      return mk(R.div, [
        mk(R.add, [mk(R.mul, [D(a), b]), mk(R.neg, [mk(R.mul, [a, D(b)])])]),
        mk(R.pow, [b, num(2)]),
      ]);
    case 'pow': {
      if (freeOf(b, v)) return mk(R.mul, [b, D(a), mk(R.pow, [a, mk(R.add, [b, num(-1)])])]);
      // aᵇ = e^(b ln a) ; la base e : (eᵘ)′ = u′eᵘ
      if (a.t === 'Sym' && a.name === R.e) return mk(R.mul, [D(b), n]);
      const lnA = R.fn.ln ? mk(R.call, [a], R.fn.ln) : null;
      if (!lnA) return num(NaN);
      return mk(R.mul, [n, D(mk(R.mul, [b, lnA]))]);
    }
    case 'sqrt':
      return mk(R.div, [D(a), mk(R.mul, [num(2), n])]);
    case 'derive':
      return D(derive(a, v, table));
    case 'call': {
      const f = table.syntax.functions[n.name]?.eval;
      const u = a,
        du = D(u);
      const call = (name, x) => mk(R.call, [x], name);
      if (f === 'exp') return mk(R.mul, [du, n]);
      if (f === 'ln') return mk(R.div, [du, u]);
      if (f === 'sqrt') return mk(R.div, [du, mk(R.mul, [num(2), n])]);
      if (f === 'sin' && R.fn.cos) return mk(R.mul, [du, call(R.fn.cos, u)]);
      if (f === 'cos' && R.fn.sin) return mk(R.neg, [mk(R.mul, [du, call(R.fn.sin, u)])]);
      // (arctan u)′ = u′ ÷ (1 + u²) ; (arcsin u)′ = u′ ÷ √(1 − u²) ; (arccos u)′ = −u′ ÷ √(1 − u²)
      if (f === 'arctan') return mk(R.div, [du, mk(R.add, [num(1), mk(R.pow, [u, num(2)])])]);
      if (f === 'arcsin' || f === 'arccos') {
        const q = mk(R.div, [
          du,
          mk(R.sqrt, [mk(R.add, [num(1), mk(R.neg, [mk(R.pow, [u, num(2)])])])]),
        ]);
        return f === 'arcsin' ? q : mk(R.neg, [q]);
      }
      return { t: 'Num', p: NaN, q: 1 };
    }
  }
  return { t: 'Num', p: NaN, q: 1 };
}

// ── Limites ───────────────────────────────────────────────────────────────
// Valeur étendue : rationnel exact, flottant, ±Infinity. Opérations « sur les limites » du cours, sans FI.
const ext = {
  add(a, b) {
    if (isInf(a) && isInf(b) && a !== b) return { fi: '∞ − ∞' };
    return PRIMS.add(a, b);
  },
  mul(a, b) {
    if ((isInf(a) && isZero(b)) || (isInf(b) && isZero(a))) return { fi: '0 × ∞' };
    if (isInf(a) || isInf(b)) return sgn(a) * sgn(b) * Infinity;
    return PRIMS.mul(a, b);
  },
  div(a, b) {
    if (isInf(a) && isInf(b)) return { fi: '∞ ÷ ∞' };
    if (isZero(a) && isZero(b)) return { fi: '0 ÷ 0' };
    if (isInf(b)) return num(0);
    if (isInf(a)) return sgn(a) * sgn(b) * Infinity;
    return PRIMS.div(a, b);
  },
};
const isZero = (x) => !isInf(x) && toF(x) === 0;
const sgn = (x) => Math.sign(toF(x));
const FI = (fi) => ({ fi });
const NONE = { none: true }; // pas de limite (certain) : (−2)ⁿ, c ÷ 0 de signe changeant

// Point d'approche numérique : la variable vaut t_k (k = 1, 2, …) de plus en plus près du point
function approach(point, k, side = 1) {
  // rangs pairs ET impairs (une suite (−2)ⁿ n'a pas l'air de converger sur les seuls 10ᵏ)
  if (point === Infinity) return num(10 ** k + (k % 2));
  if (point === -Infinity) return num(-(10 ** k) - (k % 2));
  const h = num(1, 10 ** k);
  return side > 0 ? PRIMS.add(point, h) : PRIMS.add(point, PRIMS.neg(h));
}
const evalAt = (n, v, t, env, table) => {
  try {
    return evaluate(n, { ...(env || {}), [v]: t }, table);
  } catch {
    return NaN;
  }
};
// signe de l'expression près du point (pour c ÷ 0 et ln(0)) : +1, −1 ou 0 (change de signe / inconnu)
function signNear(n, v, point, env, table, side = 0) {
  const sides = isInf(point) ? [1] : side ? [side] : [1, -1];
  let s = null;
  for (const side of sides)
    for (const k of [4, 6, 8]) {
      const x = toF(evalAt(n, v, approach(point, k, side), env, table));
      if (!Number.isFinite(x) || x === 0) return 0;
      const g = Math.sign(x);
      if (s === null) s = g;
      else if (s !== g) return 0;
    }
  return s ?? 0;
}

// opts.cc : autoriser les croissances comparées ; opts.used : renvoie { cc: true } si on s'en est servi
export function limitSym(n, v, point, table, env = {}, opts = {}) {
  const R = rolesOf(table);
  const L = (x) => limitSym(x, v, point, table, env, opts);
  const val = (r) => (r && !r.fi && 'v' in r ? r.v : null);
  if (n.t === 'Num') return Number.isNaN(n.p) ? null : { v: num(n.p, n.q) };
  if (n.t === 'Sym') {
    if (n.name === v) return { v: point };
    if (n.name === R.inf) return { v: Infinity };
    const c = constValue(table.syntax.constants[n.name]);
    if (!Number.isNaN(c)) return { v: c };
    if (env && n.name in env) return { v: env[n.name] };
    return null;
  }
  if (freeOf(n, v)) {
    const x = evaluate(n, env, table);
    return isNum(x) ? { v: x } : null;
  }
  const ev = table.nodes[n.t]?.eval;
  const kids = () => {
    const out = [];
    for (const c of n.k) {
      const r = L(c);
      if (!r) return null;
      if (r.fi || r.none) return r;
      out.push(r.v);
    }
    return out;
  };
  const growthTry = (fi) => {
    if (!opts.cc || !isInf(point)) return FI(fi);
    const g = growth(n, v, table, env);
    if (!g) return FI(fi);
    if (opts.used) opts.used.cc = true;
    return { v: growthLimit(g, point) };
  };
  switch (ev) {
    case 'neg': {
      const r = L(n.k[0]);
      return r?.fi || r?.none ? r : r ? { v: PRIMS.neg(r.v) } : null;
    }
    case 'add':
    case 'mul': {
      const ks = kids();
      if (!ks) return null;
      if (ks.none) return ks;
      if (ks.fi) return ev === 'mul' ? growthTry(ks.fi) : ks;
      let acc = ks[0];
      for (let i = 1; i < ks.length; i++) {
        acc = ext[ev](acc, ks[i]);
        if (acc?.fi) return ev === 'mul' ? growthTry(acc.fi) : acc;
      }
      return { v: acc };
    }
    case 'div': {
      const ks = kids();
      if (!ks) return null;
      if (ks.none) return ks;
      if (ks.fi) return growthTry(ks.fi);
      const [a, b] = ks;
      if (isZero(b) && !isZero(a)) {
        const s = signNear(n.k[1], v, point, env, table, opts.side);
        if (!s) return NONE; // pas de limite (signe qui change)
        return { v: sgn(a) * s * Infinity };
      }
      const r = ext.div(a, b);
      return r?.fi ? growthTry(r.fi) : { v: r };
    }
    case 'pow': {
      const [ra, rb] = [L(n.k[0]), L(n.k[1])];
      if (!ra || !rb) return null;
      if (ra.fi || ra.none) return ra;
      if (rb.fi || rb.none) return rb;
      const a = ra.v,
        b = rb.v;
      // base e : e^(±∞)
      if (isInf(b)) {
        const fa = toF(a);
        if (fa === 1) return FI('1^∞');
        if (fa <= -1) return NONE; // (−2)ⁿ, (−1)ⁿ : pas de limite
        if (fa < 0) return { v: num(0) };
        return { v: b > 0 === fa > 1 ? Infinity : num(0) };
      }
      if (isInf(a)) {
        const e = toF(b);
        if (e === 0) return { v: num(1) };
        if (e < 0) return { v: num(0) };
        const odd = Number.isInteger(e) && e % 2 !== 0;
        return { v: a < 0 && !odd ? Infinity : a };
      }
      if (isZero(a) && toF(b) < 0) {
        const s = signNear(n, v, point, env, table, opts.side);
        return s ? { v: s * Infinity } : null;
      }
      const r = PRIMS.pow(a, b);
      return isNum(r) ? { v: r } : null;
    }
    case 'sqrt': {
      const r = L(n.k[0]);
      if (!r || r.fi || r.none) return r;
      if (isInf(r.v)) return r.v > 0 ? { v: Infinity } : null;
      return toF(r.v) < 0 ? null : { v: PRIMS.sqrt(r.v) };
    }
    case 'call': {
      const f = table.syntax.functions[n.name]?.eval;
      if (f === 'limit') return null;
      const r = L(n.k[0]);
      if (!r || r.fi || r.none) return r;
      const x = r.v;
      if (f === 'exp') return { v: isInf(x) ? (x > 0 ? Infinity : num(0)) : Math.exp(toF(x)) };
      if (f === 'ln') {
        if (isInf(x)) return x > 0 ? { v: Infinity } : null;
        if (isZero(x))
          return signNear(n.k[0], v, point, env, table, opts.side) > 0 ? { v: -Infinity } : null;
        return toF(x) > 0 ? { v: Math.log(toF(x)) } : null;
      }
      if (f === 'sqrt') {
        if (isInf(x)) return x > 0 ? { v: Infinity } : null;
        return toF(x) < 0 ? null : { v: Math.sqrt(toF(x)) };
      }
      if (isInf(x)) return NONE; // sin, cos en l'infini : pas de limite
      const F = FUNCS[f];
      return F ? { v: F(toF(x)) } : null;
    }
  }
  return null;
}

// Croissances comparées : une expression c · e^(E·x) · x^P · ln(x)^G (produit / quotient de ces facteurs)
export function growth(n, v, table, env = {}) {
  const R = rolesOf(table);
  const G = (x) => growth(x, v, table, env);
  if (freeOf(n, v)) {
    const c = evaluate(n, env, table);
    return isNum(c) && !isInf(c) && toF(c) !== 0 ? { c: toF(c), E: 0, P: 0, G: 0 } : null;
  }
  if (n.t === 'Sym') return { c: 1, E: 0, P: 1, G: 0 };
  const ev = table.nodes[n.t]?.eval;
  const lin = (u) => {
    // u = k·x (k constant) → k
    if (u.t === 'Sym' && u.name === v) return 1;
    const d = evaluate(derive(u, v, table), env, table);
    const k = toF(d);
    if (!Number.isFinite(k)) return null;
    // u − k·x doit être constant : vérifié en deux points
    const u1 = toF(evalAt(u, v, num(3), env, table)),
      u2 = toF(evalAt(u, v, num(7), env, table));
    return Math.abs(u2 - u1 - 4 * k) < 1e-9 && Math.abs(u1 - 3 * k) < 1e-9 ? k : null;
  };
  switch (ev) {
    case 'neg': {
      const g = G(n.k[0]);
      return g && { ...g, c: -g.c };
    }
    case 'mul':
    case 'div': {
      let acc = { c: 1, E: 0, P: 0, G: 0 };
      for (let i = 0; i < n.k.length; i++) {
        const g = G(n.k[i]);
        if (!g) return null;
        const s = ev === 'div' && i === 1 ? -1 : 1;
        acc = {
          c: s > 0 ? acc.c * g.c : acc.c / g.c,
          E: acc.E + s * g.E,
          P: acc.P + s * g.P,
          G: acc.G + s * g.G,
        };
      }
      return acc;
    }
    case 'pow': {
      const [a, b] = n.k;
      if (a.t === 'Sym' && a.name === R.e) {
        const k = lin(b);
        return k === null ? null : { c: 1, E: k, P: 0, G: 0 };
      }
      if (!freeOf(b, v)) return null;
      const e = toF(evaluate(b, env, table));
      const g = G(a);
      return g && Number.isFinite(e) ? { c: g.c ** e, E: g.E * e, P: g.P * e, G: g.G * e } : null;
    }
    case 'sqrt': {
      const g = G(n.k[0]);
      return g && { c: Math.sqrt(Math.abs(g.c)), E: g.E / 2, P: g.P / 2, G: g.G / 2 };
    }
    case 'call': {
      const f = table.syntax.functions[n.name]?.eval;
      if (f === 'exp') {
        const k = lin(n.k[0]);
        return k === null ? null : { c: 1, E: k, P: 0, G: 0 };
      }
      if (f === 'ln' && n.k[0].t === 'Sym' && n.k[0].name === v) return { c: 1, E: 0, P: 0, G: 1 };
      if (f === 'sqrt') {
        const g = G(n.k[0]);
        return g && { c: Math.sqrt(Math.abs(g.c)), E: g.E / 2, P: g.P / 2, G: g.G / 2 };
      }
      return null;
    }
  }
  return null;
}
function growthLimit(g, point) {
  const s = point > 0 ? 1 : -1;
  const E = g.E * s;
  const powSign = s < 0 && Number.isInteger(g.P) && g.P % 2 !== 0 ? -1 : 1;
  if (E > 0) return Math.sign(g.c) * powSign * Infinity;
  if (E < 0) return num(0);
  if (s < 0 && g.G) return NaN; // ln en −∞ : hors domaine
  if (g.P > 0 || (g.P === 0 && g.G > 0)) return Math.sign(g.c) * powSign * Infinity;
  if (g.P < 0 || g.G < 0) return num(0);
  return g.c;
}

// Repli NUMÉRIQUE (pour l'évaluation seulement : une ligne transformée a la même limite que la précédente)
function limitNum(n, v, point, env, table, only = 0) {
  const sides = isInf(point) ? [1] : only ? [only] : [1, -1];
  const res = [];
  for (const side of sides) {
    const vals = [];
    for (let k = 1; k <= 12; k++) {
      const x = toF(evalAt(n, v, approach(point, k, side), env, table));
      if (Number.isNaN(x)) continue;
      vals.push(x);
    }
    if (vals.length < 4) return NaN;
    const [a, b, c] = vals.slice(-3);
    let r;
    if (
      [a, b, c].every((x) => Math.abs(x) > 1e6) &&
      Math.sign(a) === Math.sign(c) &&
      Math.abs(c) >= Math.abs(b) &&
      Math.abs(b) >= Math.abs(a)
    )
      r = Math.sign(c) * Infinity;
    else if (
      Math.abs(c - b) <= 1e-6 * Math.max(1, Math.abs(c)) &&
      Math.abs(c - b) <= Math.abs(b - a) + 1e-12
    )
      r = snap(c);
    else return NaN;
    res.push(r);
  }
  if (
    res.length === 2 &&
    !(isInf(res[0])
      ? res[0] === res[1]
      : !isInf(res[1]) && Math.abs(toF(res[0]) - toF(res[1])) < 1e-9)
  )
    return NaN;
  return res[0];
}
// une valeur flottante proche d'un rationnel simple (dénominateur ≤ 1000) devient ce rationnel
function snap(x) {
  for (let q = 1; q <= 1000; q++) {
    const p = Math.round(x * q);
    if (Math.abs(p / q - x) < 1e-6 * Math.max(1, Math.abs(x))) return num(p, q);
  }
  return x;
}

// Valeur de lim(expr ; point) : symbolique (croissances comparées comprises), sinon numérique. Le point est évalué.
// une limite nulle en un point fini, de signe constant à l'approche : 0⁺ / 0⁻ (zéro signé) ; sinon la valeur telle quelle
export function signedZero(r, expr, v, point, env, table, side = 0) {
  if (isInf(point) || !isNum(r) || toF(r) !== 0) return r;
  const s = signNear(expr, v, point, env, table, side);
  return s > 0 ? 0 : s < 0 ? -0 : r;
}
// Point d'une limite : « +∞ », « 2 », « 2⁺ » (à droite, eval `right`), « 2⁻ » (à gauche, eval `left`)
export function pointOf(pointAst, table, env = {}) {
  if (!pointAst) return { point: Infinity, side: 0 };
  const ev = table.nodes[pointAst.t]?.eval;
  const side = ev === 'right' ? 1 : ev === 'left' ? -1 : 0;
  const x = evaluate(side ? pointAst.k[0] : pointAst, env, table);
  return { point: isNum(x) ? (typeof x === 'number' ? x : x) : NaN, side };
}
export function limitValue(expr, pointAst, env, table) {
  const { point, side } = pointOf(pointAst, table, env);
  if (!isNum(point)) return NaN;
  const v = varOf(expr, table);
  if (!v) {
    const x = evaluate(expr, env, table);
    return isNum(x) ? x : NaN;
  }
  const inner = { ...(env || {}) };
  delete inner[v];
  const s = limitSym(expr, v, point, table, inner, { cc: true, side });
  if (s?.none) return NaN;
  const r = s && !s.fi ? s.v : limitNum(expr, v, point, inner, table, side);
  return signedZero(r, expr, v, point, inner, table, side);
}

// ── Outils pour les gestes (rewrite.js) ───────────────────────────────────
export const isInfinite = isInf;
// zéro signé (limite en un point fini, de signe constant) → « 0⁺ » / « 0⁻ »
export function sideZeroAst(x, table) {
  const t = Object.keys(table.nodes).find(
    (k) => table.nodes[k].eval === (Object.is(x, -0) ? 'left' : 'right'),
  );
  return t ? mk(t, [num(0)]) : extendedAst(num(0), table);
}
export function extendedAst(x, table) {
  const R = rolesOf(table);
  if (isInf(x)) {
    const s = { t: 'Sym', name: R.inf };
    return x > 0 ? s : mk(R.neg, [s]);
  }
  if (typeof x === 'number' && Number.isFinite(x)) {
    const q = snap(x);
    if (typeof q !== 'object') return null;
    x = q;
  }
  if (typeof x === 'object' && x && 'p' in x && typeof x.p === 'number') {
    const a = num(Math.abs(x.p), x.q);
    const body = x.q === 1 ? a : mk(R.div, [num(Math.abs(x.p)), num(x.q)]);
    return x.p < 0 ? mk(R.neg, [body]) : body;
  }
  return null;
}

// Forme indéterminée d'une écriture sur les limites (« lim(3n²) + lim(−5n) », « +∞ − ∞ », « 0 × (+∞) ») :
// le nom de la forme (« ∞ − ∞ »…), ou null. Les limites et les nombres sont évalués, les opérations faites en
// réels étendus.
export function indeterminateOf(n, table, env = {}) {
  let fi = null;
  const go = (x) => {
    if (fi) return NaN;
    const ev = table.nodes[x.t]?.eval;
    if (isLeaf(x) || (ev === 'call' && table.syntax.functions[x.name]?.eval === 'limit'))
      return evaluate(x, env, table);
    if (ev === 'add' || ev === 'mul') {
      let acc = go(x.k[0]);
      for (let i = 1; i < x.k.length; i++) {
        const r = ext[ev](acc, go(x.k[i]));
        if (r?.fi) {
          fi = r.fi;
          return NaN;
        }
        acc = r;
      }
      return acc;
    }
    if (ev === 'div') {
      const r = ext.div(go(x.k[0]), go(x.k[1]));
      if (r?.fi) {
        fi = r.fi;
        return NaN;
      }
      return r;
    }
    if (ev === 'neg') return PRIMS.neg(go(x.k[0]));
    return evaluate(x, env, table);
  };
  go(n);
  return fi;
}
